import { EProjectType, IComponent, isAppSupportTypeOfferedOnScreen } from "@kuika/kuika-cl-model";
import { isReactNativeGenerationOffered } from "../../components/design-tools/app-settings/react-native-feature-gate";

export interface IChildTargetSupportContext {
  components: IComponent[] | undefined;
  projectType: EProjectType | undefined;
  isWebScreen: boolean;
  /** RN_BETA_ALLOWLIST gate; resolved from the session when the caller does not pin it. */
  reactNativeOffered?: boolean;
}

/**
 * A child component can be restricted to one app target. MixedChart's `Bar`
 * series (KOA-18063) is the motivating case: it renders only in React, because
 * the Android/iOS MixedChart is a WebView whose `MIXED_CHART_TEMPLATE` carries
 * line and point geometries but no bar geometry.
 *
 * The parent's `childrenInfo` records only name/count/fixed, so the child's own
 * `appSupportType` has to be resolved from the component set. The rule itself is
 * the shared `isAppSupportTypeOfferedOnScreen` — the same predicate the palette,
 * drop/paste guards and property panel use — including the web-screen escape
 * hatch: a Web-only component IS allowed inside a mobile project when the screen
 * itself is a web screen.
 *
 * Unknown child names return `true` — this gate exists to hide an unsupported
 * child, never to hide one whose definition merely failed to load.
 */
export function isChildComponentSupportedByProjectTarget(
  context: IChildTargetSupportContext,
  childName: string
): boolean {
  const childComponent: IComponent | undefined = context.components?.find(
    (component: IComponent) => component.name === childName
  );
  if (!childComponent) {
    return true;
  }

  return isAppSupportTypeOfferedOnScreen({
    appSupportType: childComponent.appSupportType,
    projectType: context.projectType,
    isWebScreen: context.isWebScreen,
    reactNativeOffered: context.reactNativeOffered ?? isReactNativeGenerationOffered()
  });
}
